import React, { useCallback, useState } from 'react'
import { Listbox } from '@headlessui/react'
import clsx from 'clsx'
import Checkbox from '@mui/material/Checkbox'

export interface Option {
  label: string
  value: string
}

interface SelectAllMultiSelectProps<T = Option> {
  options: T[]
  onChange: (t: T[]) => void
  defaultSelected?: string[]
  getOptionValue: (t: T) => string
  getOptionLabel: (t: T) => string
  placeholder?: string
  fieldLabel: string
}

// Helper function to get default selected options
const getDefaultSelectedOptions = <T = Option,>({
  options,
  defaultSelected = [],
  getOptionValue,
}: Pick<
  SelectAllMultiSelectProps<T>,
  'options' | 'defaultSelected' | 'getOptionValue'
>) => {
  return options.filter((opt) => defaultSelected.includes(getOptionValue(opt)))
}

// Helper function to sort options
const sortOptions = <T = Option,>(
  options: T[],
  priority: string[],
  getOptionValue: (t: T) => string
) => {
  return [...options].sort((a, b) => {
    const aIndex = priority.indexOf(getOptionValue(a))
    const bIndex = priority.indexOf(getOptionValue(b))

    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex
    }
    if (aIndex !== -1) return -1
    if (bIndex !== -1) return 1

    return getOptionValue(a).localeCompare(getOptionValue(b))
  })
}

export const SelectAllMultiSelect = <T = Option,>({
  options,
  onChange,
  getOptionValue,
  getOptionLabel,
  defaultSelected = [],
  placeholder = 'Select an option',
  fieldLabel,
}: SelectAllMultiSelectProps<T>) => {
  const [state, setState] = useState(
    getDefaultSelectedOptions({ options, defaultSelected, getOptionValue })
  )

  const isSelectAll = state.length === options.length

  const handleChange = useCallback(
    (values: T[]) => {
      setState(values)
      onChange(values)
    },
    [onChange]
  )

  const handleOptionToggle = (option: T) => {
    const exists = state.find((item) => getOptionValue(item) === getOptionValue(option))
    if (exists) {
      const newState = state.filter(
        (item) => getOptionValue(item) !== getOptionValue(option)
      )
      setState(newState)
      onChange(newState)
    } else {
      const newState = [...state, option]
      setState(newState)
      onChange(newState)
    }
  }

  const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation(); // Prevent Listbox from closing
    if (isSelectAll) {
      setState([]) // Deselect all
      onChange([])
    } else {
      setState(options) // Select all
      onChange(options)
    }
  }

  // Sort options with priority (align with Insights label-type order: visual types first, then text, tag, hook, headline)
  const priorityOrder = [
    'IMAGE',
    'VIDEO',
    'DOMINANT_COLOR',
    'NOUN',
    'PHRASE',
    'TAG',
    'VERB',
    'HOOK_TEXT',
    'HEADLINE',
  ]
  const sortedOptions = sortOptions(options, priorityOrder, getOptionValue)

  return (
    <Listbox value={state} onChange={handleChange} multiple>
      {({ open }) => (
        <>
          <Listbox.Label className="block text-sm text-gray-900">
            {fieldLabel}
          </Listbox.Label>

          <div className="relative">
            <Listbox.Button className="focus:ring-[#0b6d5e] relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 sm:text-sm sm:leading-6">
              {state.map(getOptionLabel).join(', ') || placeholder}
            </Listbox.Button>
            {open && (
              <div className="mt-2 absolute z-10 w-60">
                <Listbox.Options className="w-full rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  {/* Select/Deselect All option */}
                  <Listbox.Option
                    value="select-all"
                    disabled // This option is just a label, not a selectable option
                    className="text-sm text-gray-900 bg-gray-100 cursor-default py-2 px-3 font-medium"
                  >
                    <div className="flex items-center">
                      <Checkbox
                        checked={isSelectAll}
                        onChange={handleSelectAllChange}
                        className="mr-2"
                      />
                      <label className="text-sm text-gray-900">Select/Deselect All</label>
                    </div>
                  </Listbox.Option>

                  {/* Regular options */}
                  {sortedOptions.map((option) => {
                    const isSelected = !!state.find(
                      (item) => getOptionValue(item) === getOptionValue(option)
                    )
                    return (
                      <Listbox.Option
                        key={getOptionValue(option)}
                        value={option}
                        className={({ active }) =>
                          clsx(
                            'relative cursor-default select-none py-2 pl-3 pr-9',
                            active ? 'bg-[#0b6d5e]-100 text-black' : 'text-gray-900'
                          )
                        }
                      >
                        <div className="flex items-center">
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleOptionToggle(option)}
                            className="mr-2"
                          />
                          <span
                            className={clsx(
                              'block truncate',
                              isSelected ? 'font-semibold' : 'font-normal'
                            )}
                          >
                            {getOptionLabel(option)}
                          </span>
                        </div>
                      </Listbox.Option>
                    )
                  })}
                </Listbox.Options>
              </div>
            )}
          </div>
        </>
      )}
    </Listbox>
  )
}
